/**
 * Bulk apply bar (work date, campaign, ordered apply)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { Button, Input, SearchableSelect, Switch } from '@/components/ui';

import { PresetsModalBulkRoutingRow } from './PresetsModalBulkRoutingRow';

export interface PresetsModalBulkBarProps {
  bulkWorkDate: string;
  onBulkWorkDateChange: (v: string) => void;
  bulkCampaignId: number;
  onBulkCampaignChange: (id: number) => void;
  campaigns: Array<{ id: number; name: string }>;
  matchListOrder: boolean;
  onMatchListOrderChange: (v: boolean) => void;
  selectedCount: number;
  /** Total template drops across selected presets (fixed rows + rule-based minimum). */
  selectedDropCount: number;
  disabled: boolean;
  onApplySelected: () => void;
  /** Show gateway + TLD when any preset may need them for rule-based apply. */
  showRoutingRow: boolean;
  gateways: Array<{ id: number; name: string; pricePerSms?: number | null }>;
  tlds: Array<{ value: string; displayName: string }>;
  bulkRouteId: number;
  onBulkRouteIdChange: (id: number) => void;
  bulkShortLinkTld: string;
  onBulkShortLinkTldChange: (v: string) => void;
  /** Selected bulk presets include rule-based without template routing. */
  selectionNeedsRouting: boolean;
}

export const PresetsModalBulkBar: FC<PresetsModalBulkBarProps> = ({
  bulkWorkDate,
  onBulkWorkDateChange,
  bulkCampaignId,
  onBulkCampaignChange,
  campaigns,
  matchListOrder,
  onMatchListOrderChange,
  selectedCount,
  selectedDropCount,
  disabled,
  onApplySelected,
  showRoutingRow,
  gateways,
  tlds,
  bulkRouteId,
  onBulkRouteIdChange,
  bulkShortLinkTld,
  onBulkShortLinkTldChange,
  selectionNeedsRouting,
}) => {
  const routingOk =
    !selectionNeedsRouting ||
    (bulkRouteId >= 1 &&
      bulkShortLinkTld.trim().length > 0 &&
      gateways.length > 0 &&
      tlds.length > 0);
  const applyDisabled =
    disabled || selectedCount < 1 || bulkCampaignId < 1 || !routingOk;

  return (
    <div className="flex shrink-0 flex-col gap-3 border-t border-border bg-muted/10 px-4 py-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[140px] flex-col gap-1">
          <span className="text-[10px] font-medium uppercase text-muted-foreground">Work date</span>
          <Input
            type="date"
            value={bulkWorkDate}
            onChange={(e) => onBulkWorkDateChange(e.target.value)}
            className="h-9"
          />
        </div>
        <div className="flex min-w-[160px] flex-1 flex-col gap-1">
          <span className="text-[10px] font-medium uppercase text-muted-foreground">Campaign</span>
          <SearchableSelect
            className="h-9 rounded-md"
            options={[
              { value: 0, label: 'Select campaign' },
              ...campaigns.map((c) => ({ value: c.id, label: c.name })),
            ]}
            value={bulkCampaignId}
            onChange={(v) => onBulkCampaignChange(Number(v))}
            placeholder="Select campaign"
            disabled={campaigns.length === 0}
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 pb-2">
          <Switch checked={matchListOrder} onCheckedChange={onMatchListOrderChange} />
          <span className="text-xs text-muted-foreground">Match list order on board</span>
        </label>
      </div>
      {showRoutingRow ? (
        <PresetsModalBulkRoutingRow
          gateways={gateways}
          tlds={tlds}
          bulkRouteId={bulkRouteId}
          onBulkRouteIdChange={onBulkRouteIdChange}
          bulkShortLinkTld={bulkShortLinkTld}
          onBulkShortLinkTldChange={onBulkShortLinkTldChange}
        />
      ) : null}
      <Button
        type="button"
        size="sm"
        className="w-full sm:w-auto"
        disabled={applyDisabled}
        onClick={onApplySelected}
      >
        Apply {selectedCount} preset{selectedCount !== 1 ? 's' : ''} ({selectedDropCount} drop
        {selectedDropCount !== 1 ? 's' : ''}) in order
      </Button>
    </div>
  );
};
